import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, { Component, ErrorInfo, useEffect } from 'react';
import { isProd } from '~/constants/environment';
import { globalError, sectionError } from '~/constants/errorBoundaryEvents';
import { generateErrorLogger } from '~/utilities/loggers';
import ErrorBoundaryFallbackComponent, {
  OwnProps as ErrorBoundaryFallbackComponentProps
} from './ErrorBoundaryFallbackComponent';

function ForceSectionError({ sectionId }: { sectionId?: string }) {
  useEffect(() => {
    if (isProd) return;

    // Force a section error to occur for testing purposes
    const urlParams = new URLSearchParams(window.location.search);
    const shouldForceError = urlParams
      .getAll('forceSectionError')
      .includes(sectionId ?? '');

    if (shouldForceError) {
      throw new Error(`Forced error for section: ${sectionId}`);
    }
  }, [sectionId]);

  return null;
}

type OwnProps = {
  children: React.ReactNode;
  FallbackComponent?: React.ComponentType<ErrorBoundaryFallbackComponentProps>;
  onError?: (
    _error: Error,
    _errorInfo: ErrorInfo & { section: string }
  ) => void;
  sectionId?: string;
};

type OwnState = {
  error?: Error;
  info?: ErrorInfo;
};

const logError = generateErrorLogger(globalError);

class ErrorBoundary extends Component<OwnProps, OwnState> {
  static contextType = AnalyticsContext;

  declare context: React.ContextType<typeof AnalyticsContext>;

  constructor(props) {
    super(props);

    this.state = {
      error: undefined,
      info: undefined
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { sectionId } = this.props;
    const { trackEvent } = this.context;
    const { onError = logError } = this.props;
    const payload = {
      error: error.message,
      stack: error.stack
    };
    const section = sectionId ? sectionId : 'unknown';
    const context = { ...info, section };
    trackEvent('criticalAppError', payload);

    try {
      onError(error, context);
    } catch (ignoreError) {
      logError(error, { location: 'Error boundary' });
    }

    this.setState({ error, info });
  }

  render() {
    const { children, FallbackComponent = ErrorBoundaryFallbackComponent } =
      this.props;
    const { error, info } = this.state;

    return error ? (
      <FallbackComponent
        componentStack={info?.componentStack ?? ''}
        error={error}
      />
    ) : (
      children
    );
  }
}

export default ErrorBoundary;

const logSectionError = generateErrorLogger(sectionError);

export const withErrorBoundary =
  <P,>(
    ChildComponent: React.ComponentType<P>,
    FallbackComponent: OwnProps['FallbackComponent'] = (
      _: ErrorBoundaryFallbackComponentProps
    ) => null,
    onError: OwnProps['onError'] = logSectionError,
    sectionId?: string
  ) =>
  props => (
    <ErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={onError}
      sectionId={sectionId}
    >
      <ForceSectionError sectionId={sectionId} />
      <ChildComponent {...props} />
    </ErrorBoundary>
  );
